import { useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Dialog,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Switch,
  Typography,
  DialogContent,
  FormHelperText,
} from "@mui/material";
//Import from project
import "../../Style/dialogstyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import usePackagePriceForm from "./Hooks/usePackagePriceForm";
import { SelectClassGroupFee } from "../Include/DynamicSelect/DynamicSelect";

const DISCOUNT_TYPES = [
  { value: "amount", label: "Amount ($)" },
  { value: "percent", label: "Percent (%)" },
];

export default function PackagePriceForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  classId,
  hideBackdrop = false,
}) {
  // Change Language
  const { t, language } = useContext(AuthContext);

  //fromik
  const CheckValidation = Yup.object().shape({
    title: Yup.string().required(t("required")),
    groupFeeId: Yup.object().shape({
      id: Yup.string().required(t("required")),
      title: Yup.string(),
    }),
    discountType: Yup.string(),
    discountAmount: Yup.number().min(0),
    duration: Yup.string().required(t("required")),
    unlimited: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      groupFeeId: { id: "", title: "" },
      duration: "",
      discountType: "amount",
      discountAmount: 0,
      unlimited: false,
    },
    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      if (dialogTitle === "Create") {
        createPackagePrice({
          variables: {
            input: {
              ...values,
              classId: classId,
              groupFeeId: values?.groupFeeId?.id,
              discountAmount: parseFloat(values?.discountAmount),
              unlimited: Boolean(values.unlimited),
            },
          },
        });
      } else {
        // console.log("values::", values);
        updatePackagePrice({
          variables: {
            id: editData?._id,
            input: {
              ...values,
              classId: classId,
              groupFeeId: values?.groupFeeId?.id,
              discountAmount: parseFloat(values?.discountAmount),
              unlimited: Boolean(values.unlimited),
            },
          },
        });
      }
    },
  });

  const {
    errors,
    values,
    touched,
    resetForm,
    handleSubmit,
    setFieldValue,
    getFieldProps,
  } = formik;

  const { createPackagePrice, loading, updatePackagePrice, setLoading } =
    usePackagePriceForm({
      resetForm,
      setRefetch,
      handleClose,
    });

  useEffect(() => {
    if (dialogTitle === "Update" && editData) {
      setFieldValue("unlimited", editData?.unlimited);
      setFieldValue("title", editData?.title);
      setFieldValue("discountType", editData?.discountType);
      setFieldValue("discountAmount", editData?.discountAmount ?? 0);
      setFieldValue("duration", editData?.duration);
      // setFieldValue("classId", {
      //   id: editData?.classId?._id,
      //   title:
      //     language === "en"
      //       ? editData?.classId?.courseId?.enName +
      //         " " +
      //         editData?.classId?.className
      //       : editData?.classId?.courseId?.enName +
      //         " " +
      //         editData?.classId?.className,
      // });
      setFieldValue("groupFeeId", {
        id: editData?.groupFeeId?._id,
        title:
          editData?.groupFeeId?.title +
          ": " +
          editData?.groupFeeId?.price +
          " $",
      });
    }

    if (dialogTitle === "Create" && open) {
      resetForm();
    }
  }, [open, editData]);

  return (
    <Dialog
      open={open}
      className="dialog-container"
      maxWidth="sm"
      fullWidth
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_package_price")
            : t("modal_title_update_package_price")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_title")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("thead_title")}
                  {...getFieldProps("title")}
                  error={Boolean(touched.title && errors.title)}
                  helperText={touched.title && errors.title}
                />
              </Grid>

              <Grid item xs={12}>
                {/* <Typography className={t("class-name-field-title")}>
                  {t("thead_group_fee")}
                </Typography> */}
                <SelectClassGroupFee
                  value={values?.groupFeeId}
                  onChange={(val) => {
                    setFieldValue(
                      "groupFeeId",
                      val || { id: "", title: "", price: "" },
                    );
                  }}
                />
                {!!errors.groupFeeId?.id && touched.groupFeeId?.id && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.groupFeeId?.id}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_unlimited")}
                </Typography>
                <Switch
                  checked={values.unlimited}
                  onChange={(e) => setFieldValue("unlimited", e.target.checked)}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_duration")} ({t("thead_day")})
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("thead_duration")}
                  type="number"
                  value={values?.duration}
                  onChange={(e) => {
                    const inputVal = parseFloat(e.target.value);
                    if (inputVal <= 0) {
                      setFieldValue("duration", 0);
                    } else {
                      setFieldValue("duration", inputVal);
                    }
                  }}
                  error={Boolean(touched.duration && errors.duration)}
                  helperText={touched.duration && errors.duration}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_discount_type")}
                </Typography>
                <FormControl fullWidth size="small" className="text-field">
                  <Select {...getFieldProps("discountType")}>
                    {DISCOUNT_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {touched.discountType && errors.discountType && (
                  <FormHelperText error>{errors.discountType}</FormHelperText>
                )}
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_discount_amount")}
                </Typography>
                <TextField
                  type="number"
                  // inputProps={{step : "any"}}
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("thead_discount_amount")}
                  value={values?.discountAmount}
                  onChange={(e) => {
                    const inputVal = parseFloat(e.target.value);
                    if (inputVal <= 0) {
                      setFieldValue("discountAmount", 0);
                    } else {
                      setFieldValue("discountAmount", inputVal);
                    }
                  }}
                  error={Boolean(
                    touched.discountAmount && errors.discountAmount,
                  )}
                  helperText={touched.discountAmount && errors.discountAmount}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    onClick={handleSubmit}
                    className={t("class-btn-create")}
                  >
                    {dialogTitle === "Update" ? t("update") : t("create")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
